'use client';

import React from 'react';
import { ExternalLink, Play, X } from 'lucide-react';
import ModalPortal from '@/components/ui/ModalPortal';
import { useLanguage } from '@/context/LanguageContext';
import { normalizeMediaAssetUrl, normalizeVideoUrl } from '@/lib/pageVideos';

interface ManagedVideoModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  youtubeUrl?: string | null;
  mediaUrl?: string | null;
  coverUrl?: string | null;
  eyebrow?: string;
  footer?: React.ReactNode;
}

export default function ManagedVideoModal({
  open,
  onClose,
  title,
  description,
  youtubeUrl,
  mediaUrl,
  coverUrl,
  eyebrow,
  footer,
}: ManagedVideoModalProps) {
  const { t } = useLanguage();
  if (!open) return null;

  const externalUrl = normalizeVideoUrl(youtubeUrl);
  const hostedMediaUrl = normalizeMediaAssetUrl(mediaUrl);
  const hostedCoverUrl = normalizeMediaAssetUrl(coverUrl);
  const resolvedEyebrow = eyebrow || t('pageVideo.modal.eyebrow');

  return (
    <ModalPortal>
      <div className="modal-shell-overlay items-end px-3 sm:items-center sm:px-4" onClick={onClose}>
        <div
          className="modal-shell-panel mx-0 w-full max-w-4xl overflow-hidden sm:w-full"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="border-b border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(0,204,102,0.18),transparent_34%),linear-gradient(180deg,rgba(10,16,14,0.98),rgba(7,10,17,0.98))] px-5 py-4 sm:px-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-300/80">
                  {resolvedEyebrow}
                </p>
                <div className="mt-2 flex items-center gap-2 text-white">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-brand-500/25 bg-brand-500/12 text-brand-200">
                    <Play size={18} />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-lg font-black sm:text-xl">{title}</p>
                    {description ? (
                      <p className="mt-1 text-sm text-zinc-300">{description}</p>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="flex shrink-0 flex-col items-end gap-2">
                <button type="button" onClick={onClose} className="modal-close-button">
                  <X size={16} />
                </button>
                {externalUrl ? (
                  <a
                    href={externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-amber-500/20 bg-amber-500/[0.08] px-3 py-2 text-xs font-semibold text-amber-100 transition-all hover:border-amber-400/30 hover:bg-amber-500/[0.12] hover:text-white"
                  >
                    <ExternalLink size={13} />
                    {t('pageVideo.modal.watchOnYoutube')}
                  </a>
                ) : null}
              </div>
            </div>
          </div>

          <div className="space-y-4 bg-[#080b12] px-4 py-4 sm:px-6 sm:py-5">
            <div className="overflow-hidden rounded-[28px] border border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(0,204,102,0.12),transparent_36%),linear-gradient(180deg,rgba(9,14,16,0.98),rgba(5,8,12,0.98))] p-3 shadow-[0_30px_80px_rgba(0,0,0,0.4)] sm:p-4">
              {hostedMediaUrl ? (
                <div className="overflow-hidden rounded-[22px] border border-emerald-500/15 bg-black/70">
                  <video
                    src={hostedMediaUrl}
                    poster={hostedCoverUrl || undefined}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full bg-black object-contain"
                    style={{ aspectRatio: '1920 / 1124' }}
                  />
                </div>
              ) : (
                <div className="relative flex min-h-[260px] flex-col items-center justify-center overflow-hidden rounded-[22px] border border-white/10 bg-black px-6 py-10 text-center text-sm text-zinc-300">
                  {hostedCoverUrl ? (
                    <img
                      src={hostedCoverUrl}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover opacity-35"
                    />
                  ) : null}
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(4,7,11,0.3),rgba(4,7,11,0.9))]" />
                  <div className="relative z-10 flex max-w-md flex-col items-center gap-3">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full border border-brand-500/30 bg-brand-500/12 text-brand-100">
                      <Play size={20} />
                    </span>
                    <p className="text-base font-semibold text-white">{t('pageVideo.modal.hostedUnavailableTitle')}</p>
                    <p>{t('pageVideo.modal.hostedUnavailableDescription')}</p>
                  </div>
                </div>
              )}
            </div>

          </div>

          {footer ? (
            <div className="border-t border-white/10 bg-white/[0.02] px-5 py-4 sm:px-6">{footer}</div>
          ) : null}
        </div>
      </div>
    </ModalPortal>
  );
}